"use client"

import { Button, Icon } from "@chakra-ui/react"
import classNames from "classnames"
import type { NavigationLinkItem } from "config/navigation/types/NavigationLinkItem"
import Link from "next/link"
import { usePathname } from "next/navigation"
import type React from "react"
import AppIcon from "./AppIcon"

/**
 * NavLinkItem component renders a navigation link item with customizable styles and behavior.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {NavigationLinkItem} props.item - The navigation link item data.
 * @param {string} [props.customClasses] - Optional custom CSS classes to apply to the button.
 * @param {boolean} [props.disabled=false] - Optional flag to disable the button.
 *
 * @returns {JSX.Element} The rendered navigation link item component.
 */
export const NavLinkItem: React.FC<{
    item: NavigationLinkItem
    customClasses?: string
    disabled?: boolean
}> = ({ item, customClasses, disabled = false }) => {
    const pathname = usePathname()

    /**
     * A bit unusual, but we can assume that {rooturl}/{orgGroup}/{app}/{subid} is always going to be either a dashboard or an unsubscribed page.
     * So if the URL is split by '/' and its length is 4 & the label equals 'Dashboard' we know we are on a dashboard.
     * And since we set the active state to Nav bars by matching the route within the pathname
     * This is really our only option to match a dashboard route that does not include anything in the URL after the subId.
     * */
    const dashboardPageIsActive = pathname?.split("/").length === 4 && item.label === "Dashboard"

    const handleClick = () => {
        item?.onClick && item?.onClick()
    }

    // TODO: Consider loading states
    // useEffect(() => {
    //     if (!pathname) setLoading(true)
    //     else if (pathname && loading) setLoading(false)

    //     return () => {
    //         setLoading(false)
    //     }
    // }, [searchParams, pathname])

    if (!item) return null
    return (
        <Button
            as={Link}
            href={item.path || ""}
            variant='ghost'
            size='sm'
            zIndex={10}
            className={classNames(
                "w-full flex-row items-center justify-start space-x-2 px-3 py-2.5 text-left text-base font-normal",
                customClasses,
                item.isActive && "pointer-events-none",
                item.isDisabled || disabled
                    ? "pointer-events-none bg-white text-gray-200 dark:bg-gray-800 border-none dark:text-gray-400"
                    : pathname.includes(item.styleMatchingPath) ||
                        item.isActive ||
                        dashboardPageIsActive
                      ? "text-light pointer-events-none bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:text-green-100"
                      : "text-dark hover:bg-green-50 dark:text-gray-300 dark:hover:bg-gray-500"
            )}
            onClick={handleClick}
            // isLoading={loading}
            isDisabled={item.isDisabled || disabled}
            iconSpacing={2}
            leftIcon={
                <>
                    {item.icon && <Icon as={item.icon} boxSize={6} className='text-xl' />}
                    {item.icon === undefined && item?.application && (
                        <AppIcon app={item.application} />
                    )}
                </>
            }
        >
            {item?.label}
            {item?.addOn}
        </Button>
    )
}

export default NavLinkItem
